import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { localDb, generateId, LocalTag } from '@/lib/localDatabase';

export function useLocalTags() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState<LocalTag[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const allTags = await localDb.tags.getAll();
        setTags(allTags);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching local tags:', error);
        setLoading(false);
      }
    };

    fetchTags();
  }, [refreshKey]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // Add tag
  const addTag = useCallback(async (name: string, color: string) => {
    try {
      const now = new Date().toISOString();
      const id = generateId();

      const tag: LocalTag = {
        id,
        name,
        color,
        created_at: now,
        synced: false,
      };

      await localDb.tags.add(tag);

      toast({
        title: 'Создано',
        description: 'Метка успешно добавлена',
      });

      refresh();
      return tag;
    } catch (error) {
      console.error('Error adding local tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось создать метку',
        variant: 'destructive',
      });
      return null;
    }
  }, [toast, refresh]);

  // Update tag
  const updateTag = useCallback(async (id: string, updates: Partial<LocalTag>) => {
    try {
      await localDb.tags.update(id, {
        ...updates,
        synced: false,
      });

      toast({
        title: 'Сохранено',
        description: 'Метка обновлена',
      });

      refresh();
      return true;
    } catch (error) {
      console.error('Error updating local tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось обновить метку',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, refresh]);

  // Delete tag
  const deleteTag = useCallback(async (id: string) => {
    try {
      await localDb.tags.delete(id);

      toast({
        title: 'Удалено',
        description: 'Метка удалена',
      });

      refresh();
      return true;
    } catch (error) {
      console.error('Error deleting local tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось удалить метку',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, refresh]);

  return {
    tags: tags as any[],
    loading,
    user: { id: 'local' },
    addTag,
    updateTag,
    deleteTag,
    refetch: refresh,
  };
}
